/**
 * Pure helpers for seeding the Create Workshop drawer from a workshop type's
 * catalogue defaults (BON-391).
 *
 * A workshop's length is its type's `defaultDurationMinutes`: the drawer has no
 * End field, and every end time is Start + that length.
 */

const MINUTES_PER_DAY = 24 * 60;

export const PAST_MIDNIGHT_MESSAGE = 'Starts too late to finish by midnight.';

/** Minutes-since-midnight → zero-padded "HH:MM" for a native time input. */
function toHHMM(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * "18:30" + 120 → "20:30". Null when there is nothing to derive from (no start
 * yet, or a type with no length), and null when the workshop would run past
 * midnight: a workshop is one date and two times, so it has to finish the day
 * it starts. The form reads that null as "starts too late".
 */
export function deriveEndTime(start: string, durationMinutes: number | null): string | null {
  if (!start || durationMinutes === null || durationMinutes <= 0) return null;
  const [hours, minutes] = start.split(':');
  const startMinutes = Number(hours) * 60 + Number(minutes);
  if (!Number.isFinite(startMinutes)) return null;
  const end = startMinutes + durationMinutes;
  return end < MINUTES_PER_DAY ? toHHMM(end) : null;
}

/** The minutes between two `HH:MM` times on the same day, or null if either is missing. */
export function minutesBetween(start: string, end: string): number | null {
  const toMinutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return null;
  return toMinutes(end) - toMinutes(start);
}

/** "2 hours", "1 hour", "1 hr 30 min", "45 min". */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return h === 1 ? '1 hour' : `${h} hours`;
  return h === 0 ? `${m} min` : `${h} hr ${m} min`;
}

/**
 * Integer cents → the drawer's dollar string. The price input holds a bare
 * number next to a static "$", so a whole-dollar default stays "65" while a
 * fractional one keeps its cents ("67.50"). Rounding to whole dollars here — as
 * this did before BON-391 — silently overcharged by up to 50¢.
 */
export function formatDefaultPrice(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}
